import { plantProtectionStatsApi } from '../api/plantProtectionStats';
import { getAnalyticsOverview } from '../api/analytics';

const listeners = new Set();
let cache = null;
let cacheKey = '';
let inflight = null;

import { getTenantId as getAuthTenantId } from './token.js';
import { resolveDataTenantId } from './platformViewTenant.js';

export function getTenantId() {
  return resolveDataTenantId();
}

/** 三端对齐的核心 KPI（优先 /api/analytics/overview，回退 plant-protection/stats） */
export function extractUnifiedKpis(data, analyticsOverview = null) {
  const base = (() => {
    if (!data) {
      return {
        inProgressOperations: 0,
        unresolvedWarnings: 0,
        activeCrops: 0,
        dataRefTime: null
      };
    }
    const unified = data.unifiedKpis;
    if (unified) {
      return {
        inProgressOperations: Number(unified.inProgressOperations ?? 0),
        unresolvedWarnings: Number(unified.unresolvedWarnings ?? 0),
        activeCrops: Number(unified.activeCrops ?? 0),
        dataRefTime: data.dataRefTime ?? null
      };
    }
    return {
      inProgressOperations: Number(data.operations?.inProgress ?? 0),
      unresolvedWarnings: Number(data.warnings?.unresolvedTotal ?? 0),
      activeCrops: Number(data.crops?.activeCount ?? 0),
      dataRefTime: data.dataRefTime ?? null
    };
  })();

  if (!analyticsOverview) {
    return base;
  }

  const ops = analyticsOverview.operations || {};
  const tasks = analyticsOverview.tasks || {};
  return {
    ...base,
    inProgressOperations: Number(ops.inProgressOperations ?? base.inProgressOperations),
    activeCrops: base.activeCrops > 0
      ? base.activeCrops
      : Number(analyticsOverview.cropCount ?? base.activeCrops),
    landParcelCount: Number(analyticsOverview.landParcelCount ?? 0),
    completedOperations: Number(ops.completedOperations ?? 0),
    completedTasks: Number(tasks.completedTasks ?? 0),
    planCount: Number(analyticsOverview.plans?.totalPlans ?? analyticsOverview.plans?.total ?? 0),
    dataRefTime: base.dataRefTime || new Date().toISOString()
  };
}

export function subscribePlantProtectionStats(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

/** 写操作后调用，使各页下次刷新拉取最新 stats */
export function invalidatePlantProtectionStats() {
  cache = null;
  cacheKey = '';
  inflight = null;
  notify();
}

/**
 * @param {string} scope cockpit | farm | protection
 * @param {{ force?: boolean }} options
 */
export async function fetchPlantProtectionStats(scope = 'protection', options = {}) {
  const tenantId = getTenantId();
  const key = `${tenantId}:${scope}`;
  if (!options.force && cache && cacheKey === key) {
    return cache;
  }
  if (!options.force && inflight && inflight.key === key) {
    return inflight.promise;
  }

  const promise = Promise.allSettled([
    plantProtectionStatsApi.getStats({ tenantId, scope }),
    getAnalyticsOverview({ tenantId })
  ])
    .then(([statsResult, analyticsResult]) => {
      const statsRes = statsResult.status === 'fulfilled' ? statsResult.value : null;
      const analyticsRes = analyticsResult.status === 'fulfilled' ? analyticsResult.value : null;
      const raw = statsRes?.data?.success ? statsRes.data.data : null;
      const analyticsOverview = analyticsRes?.data?.success ? analyticsRes.data.data : null;
      if (!raw && !analyticsOverview) {
        throw new Error(statsRes?.data?.message || 'stats 请求失败');
      }
      const entry = {
        raw,
        analyticsOverview,
        kpis: extractUnifiedKpis(raw, analyticsOverview),
        scope,
        tenantId
      };
      cache = entry;
      cacheKey = key;
      inflight = null;
      // 仅 invalidate 时 notify；拉取成功再 notify 会触发订阅方 force 重拉，形成死循环
      return entry;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });

  inflight = { key, promise };
  return promise;
}
